import { unzip, Unzipped, UnzipFileFilter } from 'fflate'
import { CreativeImportValidationReport, extensionOf, mediaRefs } from '@marketing-milk/interfaces'
import { RowMediaPreview } from './state'

// The report's media paths are normalized against the archive root after the
// server strips a single shared wrapper folder; the raw ZIP entries may still
// carry that wrapper. Mirror the server's rule exactly: strip the first path
// segment only when every kept entry lives under the same one — never strip
// per-entry, or an unrelated nested file (junk/images/a.png) could match a
// manifest path (images/a.png) in a mixed-root archive.

const JUNK_PREFIX = '__MACOSX/'

function isJunkEntry(name: string): boolean {
  if (name.endsWith('/')) return true // directory entry
  if (name.startsWith(JUNK_PREFIX)) return true
  const base = name.split('/').pop() ?? ''
  return base.startsWith('.')
}

/** Strip exactly one shared top-level folder when every file lives under it (mirrors the nest zip reader). */
function stripWrapperFolder(paths: string[]): (p: string) => string {
  const firstSegments = new Set(paths.map(p => p.split('/')[0]))
  if (paths.length === 0 || firstSegments.size !== 1) return p => p
  const wrapper = `${firstSegments.values().next().value}/`
  if (!paths.every(p => p.startsWith(wrapper))) return p => p
  return p => p.slice(wrapper.length)
}

// fflate's async API inflates on worker threads, keeping big archives off the
// UI thread (unzipSync could block the tab for the duration of a 500 MB batch).
function unzipAsync(buffer: Uint8Array, filter: UnzipFileFilter): Promise<Unzipped> {
  return new Promise((resolve, reject) =>
    unzip(buffer, { filter }, (err, data) => (err ? reject(err) : resolve(data)))
  )
}

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
}

function mimeFor(path: string): string {
  return MIME_BY_EXTENSION[extensionOf(path)] ?? 'image/jpeg'
}

/**
 * How a blob should be RENDERED, which is not the same question as which
 * Facebook pipeline it uploads through. A .gif goes to /advideos — so the
 * importer treats it as video — but a GIF blob in a `<video>` element won't
 * play. Preview branches on the extension; the upload branches on the format.
 */
function previewKind(path: string): 'image' | 'video' {
  const ext = extensionOf(path)
  return ext === 'mp4' || ext === 'mov' ? 'video' : 'image'
}

/** Intrinsic size of an image object URL. Resolves undefined rather than rejecting. */
function imageDimensions(url: string): Promise<{ width?: number; height?: number }> {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve({})
    img.src = url
  })
}

/**
 * Where in a video to grab the still. Produced video routinely opens on black —
 * a fade-up, a slate, a dark first beat — so a frame near zero is a black
 * square, which is what a viewer reads as "broken thumbnail". A second in clears
 * almost all of that; a quarter of the way through covers clips shorter than
 * that without running past the end.
 *
 * `duration` is NaN before metadata and Infinity for a stream, so anything
 * non-finite falls back to the flat one second.
 */
function posterTimestamp(duration: number): number {
  return Number.isFinite(duration) && duration > 0 ? Math.min(1, duration / 4) : 1
}

/** How long to wait on one video before giving up on its poster. */
const POSTER_TIMEOUT_MS = 3000

/**
 * A video has no still frame of its own, so one is drawn: seek a detached
 * `<video>` past the opening (see `posterTimestamp`) and paint it to a canvas.
 * The dimensions come from the same element — `new Image()` fails silently on a
 * video blob, so it can't be reused here.
 *
 * Resolves without a poster rather than rejecting: a video that won't decode in
 * this browser should still import, it just shows a placeholder in Review.
 */
function videoPosterAndDimensions(
  url: string
): Promise<{ posterUrl?: string; width?: number; height?: number }> {
  return new Promise(resolve => {
    const video = document.createElement('video')
    let settled = false

    // `timer` is declared below and closed over here: every path into `finish`
    // is asynchronous (an element event, or the timeout itself), so it is always
    // initialized by the time this runs.
    const finish = (result: { posterUrl?: string; width?: number; height?: number }) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      video.removeAttribute('src')
      // Detaching the source alone doesn't stop an in-flight fetch/decode.
      video.load()
      resolve(result)
    }

    const dimensions = () => ({
      width: video.videoWidth || undefined,
      height: video.videoHeight || undefined,
    })

    /** Paints the frame currently displayed. Any failure degrades to dimensions only. */
    const capture = () => {
      const size = dimensions()
      try {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const context = canvas.getContext('2d')
        if (!context || !canvas.width || !canvas.height) return finish(size)
        context.drawImage(video, 0, 0)
        finish({ posterUrl: canvas.toDataURL('image/jpeg', 0.7), ...size })
      } catch {
        // A cross-origin/tainted canvas throws on read; the blob is same-origin
        // so this shouldn't fire, but a missing poster beats a broken Review.
        finish(size)
      }
    }

    // 'auto', not 'metadata': the seek below needs frame data, and the source is
    // an in-memory blob, so there's no network cost to loading it eagerly.
    video.preload = 'auto'
    video.muted = true
    video.onerror = () => finish({})
    // Capture on 'seeked', NOT 'loadeddata'. 'loadeddata' can be queued for the
    // element's ORIGINAL position before the seek below is applied — for a local
    // blob that decodes immediately, that race is the common case, and losing it
    // captures frame zero: the black opening this seek exists to skip.
    video.onseeked = capture
    video.onloadedmetadata = () => {
      try {
        video.currentTime = posterTimestamp(video.duration)
      } catch {
        finish(dimensions())
      }
    }
    // A decode that stalls after metadata fires neither 'seeked' nor 'error'.
    // Without this the await in extractRowThumbnails never returns and NO row in
    // the import gets a preview — one bad file costing the whole batch. Capture
    // whatever the element has reached rather than discarding a usable frame.
    const timer = setTimeout(capture, POSTER_TIMEOUT_MS)
    video.src = url
  })
}

/**
 * Extracts Review previews for every media file the report references, straight
 * from a ZIP in the browser. Returns one ordered `RowMediaPreview[]` per
 * rowNumber — a single entry for single image/video rows, one per slide or
 * dynamic asset otherwise. The caller owns revoking the object URLs (see
 * `previewObjectUrls`).
 *
 * Accepts any Blob: the freshly uploaded File on first render, or the
 * re-downloaded staged archive on reopen (both unzip identically here).
 */
export async function extractRowThumbnails(
  file: Blob,
  report: CreativeImportValidationReport
): Promise<Record<number, RowMediaPreview[]>> {
  // One shared traversal of where media lives, so this can't drift from what the
  // validator resolved or what the worker uploads.
  const rowRefs = report.rows.map(row => ({ rowNumber: row.rowNumber, refs: mediaRefs(row.row) }))
  const wantedPaths = new Set(rowRefs.flatMap(row => row.refs.map(ref => ref.path)))
  if (!wantedPaths.size) return {}

  const buffer = new Uint8Array(await file.arrayBuffer())

  // Pass 1: walk entry names only (filter always declines, so nothing
  // inflates) to compute the same wrapper normalization the server applied.
  const entryNames: string[] = []
  await unzipAsync(buffer, entry => {
    entryNames.push(entry.name)
    return false
  })
  const normalize = stripWrapperFolder(entryNames.filter(name => !isJunkEntry(name)))

  // Pass 2: inflate exactly the entries whose normalized path is referenced.
  const entries = await unzipAsync(
    buffer,
    entry => !isJunkEntry(entry.name) && wantedPaths.has(normalize(entry.name))
  )

  const byPath = new Map<string, Omit<RowMediaPreview, 'ordinal'>>()
  for (const [entryName, data] of Object.entries(entries)) {
    const path = normalize(entryName)
    if (byPath.has(path)) continue
    const sourceUrl = URL.createObjectURL(new Blob([data], { type: mimeFor(path) }))
    const kind = previewKind(path)
    const decoded =
      kind === 'video'
        ? await videoPosterAndDimensions(sourceUrl)
        : await imageDimensions(sourceUrl)
    byPath.set(path, { kind, sourceUrl, ...decoded })
  }

  return Object.fromEntries(
    rowRefs
      .map(row => {
        const previews = row.refs
          .map((ref): RowMediaPreview | null => {
            const decoded = byPath.get(ref.path)
            return decoded ? { ordinal: ref.ordinal, placement: ref.placement, ...decoded } : null
          })
          .filter((preview): preview is RowMediaPreview => preview !== null)
        return [row.rowNumber, previews] as const
      })
      .filter(([, previews]) => previews.length > 0)
  )
}
